import { isNil } from "lodash";
import { useCallback } from "react";

import {
  type NotificationEngineMode,
  type NotificationEventType,
  useNotificationCutoverConfigsQuery,
  useSetNotificationCutoverModeMutation,
} from "../../generated/graphql";

/**
 * The cutover list and the mode change that keeps it current.
 *
 * This app disables the Apollo cache (`fetchPolicy: "no-cache"`), so the list
 * is refreshed by re-running its own query after a change. A `refetchQueries`
 * entry would refetch into the cache, which this query never reads, and leave
 * the table on the old mode until the page reloads.
 *
 * The list query reports errors instead of the app-wide `errorPolicy:
 * "ignore"`, so a refused request (for example a session outside a staff
 * company) shows its message rather than an empty table.
 */
export const useNotificationCutover = () => {
  const { data, loading, error, refetch } = useNotificationCutoverConfigsQuery({
    errorPolicy: "all",
  });
  const [setMode, { loading: saving, error: saveError }] =
    useSetNotificationCutoverModeMutation();

  const changeMode = useCallback(
    async (
      eventType: NotificationEventType,
      mode: NotificationEngineMode,
    ): Promise<void> => {
      try {
        const result = await setMode({
          variables: { input: { eventType, mode } },
        });
        if (isNil(result.errors) || result.errors.length === 0) {
          await refetch();
        }
      } catch {
        // Nothing is lost here: a failed save is already in `saveError` and a
        // failed refetch in the list query's `error`, and the page shows both.
        // Catching only stops the rejection from going unhandled.
      }
    },
    [setMode, refetch],
  );

  return {
    configs: data?.notificationCutoverConfigs ?? [],
    loading,
    error,
    saving,
    saveError,
    changeMode,
  };
};
